/**
 * Template Utilities (Stories 5.3, 5.14, 5.35)
 *
 * Metric-to-platform mapping, required platform extraction,
 * missing platform detection, recommendation filtering,
 * layout serialization, and platform-based widget filtering.
 *
 * CRITICAL: Platform identifiers use CANONICAL IDs matching the
 * integration API `type` field (e.g., "google-analytics-4", NOT "ga4").
 *
 * Story 5.35: METRIC_PLATFORM_MAP is now derived from the
 * integration registry (buildMetricPlatformMap) as single source of truth,
 * supplemented with metrics for adapters not yet registered (social platforms).
 */

import { buildMetricPlatformMap } from "@provenleap/integrations";

/**
 * Local type definitions mirroring @provenleap/db report-layout-types.
 * Defined locally because @provenleap/db is not a web dependency.
 */
export interface WidgetConfig {
  id: string;
  type: string;
  title: string;
  position: { row: number; col: number; colSpan: number; rowSpan?: number };
  config: Record<string, unknown>;
  autoSize?: boolean;
}

export interface TemplateLayout {
  version: 1;
  grid: { columns: 12 };
  widgets: WidgetConfig[];
  /** Multi-page layout (Story 5.32). When present, widgets come from pages. */
  pages?: { id: string; title: string; order: number; widgets: WidgetConfig[] }[];
  format?: "document" | "slide-deck";
}

/**
 * Flattens all widgets from a TemplateLayout, handling both legacy flat
 * widgets and multi-page format. Pages take precedence over flat widgets.
 */
export function getAllWidgets(layout: TemplateLayout): WidgetConfig[] {
  if (layout.pages && layout.pages.length > 0) {
    return layout.pages.flatMap((p) => p.widgets);
  }
  return layout.widgets;
}

/**
 * Supplemental metric→platform entries for adapters not yet registered.
 * These social platform metrics (Story 5.15 — top posts) are needed for
 * template matching but their adapters are from future epics (9, 10).
 * Once those adapters are registered, these entries will be superseded
 * by the registry-built map.
 */
const SUPPLEMENTAL_METRICS: Record<string, string> = {
  // Facebook Pages -> "facebook-pages" (Story 5.15 — top posts)
  fb_post_reactions: "facebook-pages",
  fb_post_impressions: "facebook-pages",
  fb_post_clicks: "facebook-pages",

  // Instagram Business -> "instagram-business" (Story 5.15 — top posts)
  ig_post_reactions: "instagram-business",
  ig_post_impressions: "instagram-business",
  ig_post_saves: "instagram-business",

  // LinkedIn Pages -> "linkedin-pages" (Story 5.15 — top posts)
  li_post_reactions: "linkedin-pages",
  li_post_impressions: "linkedin-pages",
  li_post_clicks: "linkedin-pages",
};

/**
 * Maps metric keys to their canonical platform identifiers.
 * Built from the integration adapter registry (single source of truth)
 * + supplemental entries for adapters not yet registered.
 *
 * Ambiguous metrics (clicks, impressions, ctr, conversions) are mapped to a
 * single default platform via priority ordering in buildMetricPlatformMap().
 *
 * NOTE: Evaluated at module load. Depends on adapter registration having
 * already run (the `@provenleap/integrations` barrel import triggers
 * `./google/index` side-effect registration before this executes).
 */
export const METRIC_PLATFORM_MAP: Record<string, string> = {
  ...buildMetricPlatformMap(),
  ...SUPPLEMENTAL_METRICS,
};

/** Threshold for recommending a template (>= 50% of required platforms connected). */
export const RECOMMENDED_THRESHOLD = 0.5;

/**
 * Extracts metric keys from all widget configs in a template layout.
 * Handles different widget types that store metrics differently:
 * - kpi, pie-chart: config.metricKey (string)
 * - line-chart, bar-chart: config.metrics (string[])
 * - data-table: config.columns (string[]) — filtered to only known metrics
 * - text-block, top-posts: no metric keys (top-posts uses config.platform)
 */
export function extractMetricKeys(widgets: WidgetConfig[]): string[] {
  const keys = new Set<string>();
  for (const widget of widgets) {
    // top-posts uses config.platform, not metric keys
    if (widget.type === "top-posts") continue;
    const c = widget.config as Record<string, unknown>;
    if (typeof c.metricKey === "string") keys.add(c.metricKey);
    if (Array.isArray(c.metrics))
      c.metrics.forEach((m) => typeof m === "string" && keys.add(m));
    if (Array.isArray(c.columns))
      c.columns.forEach((col) => {
        if (typeof col === "string" && METRIC_PLATFORM_MAP[col]) keys.add(col);
      });
    // Custom metric widgets store metric keys in variables[].metricKey
    if (widget.type === "custom-metric" && Array.isArray(c.variables)) {
      for (const variable of c.variables) {
        const v = variable as Record<string, unknown>;
        if (typeof v.metricKey === "string") {
          keys.add(v.metricKey);
        }
      }
    }
  }
  return Array.from(keys);
}

/** Social platform identifiers used by top-posts widgets */
const SOCIAL_PLATFORMS = new Set(["facebook-pages", "instagram-business", "linkedin-pages"]);

/**
 * Gets the unique required platform identifiers for a template layout.
 * Returns canonical platform IDs (e.g., "google-analytics-4").
 * Handles top-posts widgets which specify platform via config.platform.
 */
export function getRequiredPlatforms(layout: TemplateLayout): string[] {
  const allWidgets = getAllWidgets(layout);
  const metrics = extractMetricKeys(allWidgets);
  const platforms = new Set<string>();
  for (const metric of metrics) {
    const platform = METRIC_PLATFORM_MAP[metric];
    if (platform) platforms.add(platform);
  }
  // Add platforms required by top-posts widgets
  for (const widget of allWidgets) {
    if (widget.type === "top-posts") {
      const configPlatform = (widget.config as Record<string, unknown>).platform as string;
      if (configPlatform && configPlatform !== "all" && SOCIAL_PLATFORMS.has(configPlatform)) {
        platforms.add(configPlatform);
      } else if (!configPlatform || configPlatform === "all") {
        for (const sp of SOCIAL_PLATFORMS) platforms.add(sp);
      }
    }
  }
  return Array.from(platforms);
}

/**
 * Returns platform IDs from `required` that are NOT in `connected`.
 */
export function getMissingPlatforms(
  required: string[],
  connected: string[]
): string[] {
  const connectedSet = new Set(connected);
  return required.filter((p) => !connectedSet.has(p));
}

/**
 * Parsed template type used by gallery components.
 */
export interface ParsedTemplate {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  category: string | null;
  layoutJson: string;
  thumbnailUrl: string | null;
  isSystemTemplate: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  locked: boolean;
  parsedLayout: TemplateLayout;
  /** True when layoutJson failed to parse (malformed JSON) */
  parseError?: boolean;
  /** Report format — "document" (default) or "slide-deck" (Story 5.23) */
  format: "document" | "slide-deck";
}

/**
 * Filters templates where >= RECOMMENDED_THRESHOLD of required platforms are connected.
 * Templates with 0 required platforms are excluded from recommendations.
 */
export function getRecommendedTemplates(
  templates: ParsedTemplate[],
  connectedPlatforms: string[]
): ParsedTemplate[] {
  return templates.filter((t) => {
    const required = getRequiredPlatforms(t.parsedLayout);
    if (required.length === 0) return false;
    const matched = required.filter((p) =>
      connectedPlatforms.includes(p)
    ).length;
    return matched / required.length >= RECOMMENDED_THRESHOLD;
  });
}

// ============================================================================
// Layout Serialization (Story 5.14)
// ============================================================================

/** Runtime / client-bound config keys that must be stripped during
 *  serialization. `previewData` is editor-runtime state; `integrationIds`
 *  (Story 32.6 review R3-2) holds CLIENT-SPECIFIC `integrations` row UUIDs —
 *  a per-widget account override that is meaningless (and fail-loud: the
 *  server renders an empty-intersection error envelope) when the template is
 *  applied to any other client. Templates stay client-agnostic: applying one
 *  always starts from the default all-attachments aggregate. */
const RUNTIME_CONFIG_KEYS = new Set([
  "value",
  "data",
  "dataPoints",
  "clientId",
  "previewData",
  "integrationIds",
]);

/**
 * Serializes canvas widgets into a clean TemplateLayout for saving.
 *
 * Allowlist-based: explicitly picks ONLY {id, type, title, position, config} per widget.
 * - Strips autoSize flag (rendering hint, not layout definition)
 * - Strips runtime config keys (value, data, dataPoints, clientId)
 * - Regenerates all widget IDs with fresh crypto.randomUUID() to prevent collisions
 */
export function serializeCanvasToTemplateLayout(
  widgets: WidgetConfig[],
  format: "document" | "slide-deck" = "document"
): TemplateLayout {
  const serializedWidgets: WidgetConfig[] = widgets.map((widget) => {
    // Clean config: strip runtime keys
    const cleanConfig: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(widget.config)) {
      if (!RUNTIME_CONFIG_KEYS.has(key)) {
        cleanConfig[key] = val;
      }
    }

    // Allowlist extraction with fresh ID
    return {
      id: crypto.randomUUID(),
      type: widget.type,
      title: widget.title,
      position: {
        row: widget.position.row,
        col: widget.position.col,
        colSpan: widget.position.colSpan,
        ...(widget.position.rowSpan !== undefined
          ? { rowSpan: widget.position.rowSpan }
          : {}),
      },
      config: cleanConfig,
    };
  });

  return {
    version: 1,
    grid: { columns: 12 },
    widgets: serializedWidgets,
    format,
  };
}

/**
 * Maps a single canonical platform ID to a report template category.
 */
const PLATFORM_TO_CATEGORY: Record<string, string> = {
  "google-analytics-4": "analytics",
  "google-search-console": "seo",
  "google-ads": "advertising",
  "youtube-analytics": "social",
  "google-business-profile": "analytics",
  "facebook-pages": "social",
  "instagram-business": "social",
  "linkedin-pages": "social",
};

/**
 * Auto-detects a category from widget metrics.
 * If mixed platforms → "multi-channel", single platform → mapped category.
 */
export function detectCategoryFromWidgets(
  widgets: WidgetConfig[]
): string | null {
  const platforms = getRequiredPlatforms({ version: 1, grid: { columns: 12 }, widgets });
  if (platforms.length === 0) return null;
  if (platforms.length > 1) return "multi-channel";
  const first = platforms[0];
  if (!first) return null;
  return PLATFORM_TO_CATEGORY[first] ?? null;
}

/**
 * Filters widgets to only those whose required platforms are all connected.
 * Used by MissingIntegrationsDialog "Remove Incompatible Widgets" action.
 * For top-posts: platform="all" requires at least one social platform connected;
 * specific platform requires that exact platform connected.
 */
export function filterWidgetsByAvailablePlatforms(
  widgets: WidgetConfig[],
  connectedPlatforms: string[]
): WidgetConfig[] {
  const connectedSet = new Set(connectedPlatforms);
  return widgets.filter((widget) => {
    // Handle top-posts widgets specially
    if (widget.type === "top-posts") {
      const configPlatform = (widget.config as Record<string, unknown>).platform as string;
      if (configPlatform && configPlatform !== "all" && SOCIAL_PLATFORMS.has(configPlatform)) {
        return connectedSet.has(configPlatform);
      }
      // "all" or missing: keep if at least one social platform is connected
      for (const sp of SOCIAL_PLATFORMS) {
        if (connectedSet.has(sp)) return true;
      }
      return false;
    }

    const metrics = extractMetricKeys([widget]);
    const required = new Set<string>();
    for (const metric of metrics) {
      const platform = METRIC_PLATFORM_MAP[metric];
      if (platform) required.add(platform);
    }
    // Keep widget if it has no platform requirements or all are connected
    if (required.size === 0) return true;
    for (const platform of required) {
      if (!connectedSet.has(platform)) return false;
    }
    return true;
  });
}
